import { DateTime } from '@ksyos/date-time';
import {
    Loader,
    Spacer,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IContractSettingLog } from '@common/interfaces/contractSettingLog';
import { ContractSettingLogModifiedField } from '@common/enums/contractSettingLogModifiedField';
import { getDisplayName } from '@common/utils/getDisplayName';
import { never } from '@common/utils/never';
import { FormatCurrency } from '@frontend/components/elements/FormatCurrency';
import { ListPager } from '@frontend/components/elements/ListPager';
import { translateKey } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';

interface IOrganizationContractSettingLogsTableProps {
    episodeCategoryId: string;
    organizationId: string;
}

export function OrganizationContractSettingLogsTable(props: IOrganizationContractSettingLogsTableProps) {
    const abortableFetch = useAbortableFetch();
    const [isLoading, setIsLoading] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [organizationContractSettingLogs, setOrganizationContractSettingLogs] = useState<IContractSettingLog[]>([]);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        const getContractSettingLogs = async () => {
            setIsLoading(true);
            await fetchOrganizationContractSettingLogs();
            setIsLoading(false);
        };

        getContractSettingLogs();
    }, [props.organizationId, props.episodeCategoryId, itemsPerPage, currentPage]);

    return (
        <>
            {isLoading && (
                <Spacer align="center">
                    <Loader size="medium" />
                </Spacer>
            )}
            {!isLoading && (
                <>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{translateKey('contractSettingLogs.modificationDate')}</TableHeader>
                                    <TableHeader>{translateKey('contractSettingLogs.modifiedBy')}</TableHeader>
                                    <TableHeader>{translateKey('contractSettingLogs.serviceType')}</TableHeader>
                                    <TableHeader>{translateKey('contractSettingLogs.modificationType')}</TableHeader>
                                    <TableHeader>{translateKey('contractSettingLogs.newValue')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organizationContractSettingLogs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell>{DateTime.from(log.created_at).format('DD-MM-YYYY')}</TableCell>
                                        <TableCell>
                                            {log.modifierPractitioner.id
                                                ? getDisplayName(log.modifierPractitioner)
                                                : 'Via query'}
                                        </TableCell>
                                        <TableCell>{translateKey(`serviceTypeEnum.${log.serviceType}`)}</TableCell>
                                        <TableCell>
                                            {translateKey(`contractSettingLogs.modifiedFields.${log.modifiedField}`)}
                                        </TableCell>
                                        <TableCell>
                                            {renderContractSettingLogModifiedField(log.modifiedField, log.newValue)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ListPager
                        itemsPerPage={itemsPerPage}
                        onChangeItemsPerPage={(value) => {
                            setItemsPerPage(value);
                            setCurrentPage(1);
                        }}
                        onChangePage={setCurrentPage}
                        page={currentPage}
                        total={total}
                    />
                </>
            )}
        </>
    );

    async function fetchOrganizationContractSettingLogs() {
        const url = `/api/contractSettingLogs?organizationId=${props.organizationId}&episodeCategoryId=${
            props.episodeCategoryId
        }&limit=${itemsPerPage}&offset=${(currentPage - 1) * itemsPerPage}`;

        const response = await abortableFetch.getJson(url);
        setTotal(response.json.total);
        setOrganizationContractSettingLogs(response.json.items);
    }

    function renderContractSettingLogModifiedField(modifiedField: string, newValue: string | null) {
        switch (modifiedField) {
            case ContractSettingLogModifiedField.contractPrice:
                return (
                    <FormatCurrency
                        maximumFractionDigits={4}
                        value={Number(newValue)}
                        valueIsDecimal={true}
                    />
                );
            case ContractSettingLogModifiedField.startDate:
                return <>{DateTime.from(newValue as string).format('DD-MM-YYYY')}</>;
            case ContractSettingLogModifiedField.endDate:
                return <>{newValue ? DateTime.from(newValue).format('DD-MM-YYYY') : '-'}</>;
            default:
                never(modifiedField as never);
        }
    }
}
